import React, { useState, useEffect } from 'react';
import { usePaginationV2 } from '@uhg-abyss/web/hooks/usePagination';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { V2ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { V2StepTracker } from '@uhg-abyss/web/ui/StepTracker';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useUser } from '@src/state/user/hooks';
import { UpdateInformation } from './UpdateInformation';
import { UpdateReview } from './UpdateReview';
import { UpdateAttestation } from './UpdateAttest';
import { getFileType } from '@src/state/tinRegistration/utils';
import { event } from '@uhg-abyss/web/tools/event';
import { Loading } from '@src/common/Loading';
import { ADDR_START_ID, MAX_ADDRESSES } from './constants';
import { storage } from '@uhg-abyss/web/tools/storage';

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContainer = styled('div', {
  padding: '30px 40px',
  maxWidth: '1400px',
  margin: '0 auto',
});

const PageHeading = styled('h1', {
  margin: 0,
});

const PageText = styled('p', {
  margin: '3px 0 30px',
  fontSize: '16px',
  fontWeight: 500,
});

const ContentPanel = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
});

const ButtonRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const CancelText = styled('span', {
  fontSize: '16px',
  fontWeight: 700,
  marginLeft: '20px',
  color: '$info1',
  cursor: 'pointer',
});

// Stable references — avoids Stitches regenerating class names on every render.
const MODAL_CSS = {
  'abyss-modal-base-header': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    textAlign: 'center',
  },
  'abyss-modal-base-body': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'center',
  },
};

const STEPS = ['Update information', 'Review', 'Attest'];
const UPDATE_INFO_STEP_INDEX = 0;
const REVIEW_STEP_INDEX = 1;
const ATTEST_STEP_INDEX = 2;

const PASSWORD_PROTECTED_UPLOAD_MESSAGE =
  'Upload failed: This file is password-protected and cannot be processed. Please remove the password and try uploading again.';
const DEFAULT_UPLOAD_ERROR_MESSAGE =
  "Service unavailable: We're sorry, your upload was unsuccessful. Please try again later. (D-001)";

const ADDR_SLOT_IDS = Array.from(
  { length: MAX_ADDRESSES },
  (_, i) => ADDR_START_ID + i
);

const UPLOAD_SLOTS = [
  { model: 'w9.file', getFile: (afv) => afv.w9?.file[0] },
  ...ADDR_SLOT_IDS.map((id) => ({
    model: `secondaryServicing[${id}].file`,
    getFile: (afv) => afv.secondaryServicing?.[id]?.file[0],
  })),
];

const getUploadErrorMessage = (error) => {
  const messageCandidates = [
    error?.message,
    error?.data?.message,
    error?.response?.data?.message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const faultCodeCandidates = [
    error?.faultCode,
    error?.data?.faultCode,
    error?.response?.data?.faultCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const isPasswordProtectedError =
    /password[ -]?protected|encrypted|cannot be processed/.test(
      messageCandidates
    ) ||
    /password|encrypt/.test(faultCodeCandidates) ||
    faultCodeCandidates.includes('doduee');

  if (isPasswordProtectedError) {
    return PASSWORD_PROTECTED_UPLOAD_MESSAGE;
  }

  return error?.message || DEFAULT_UPLOAD_ERROR_MESSAGE;
};

// ─── Component ────────────────────────────────────────────────────────────────

export const UpdateSubmission = () => {
  const abyssForm = useFormV2();

  const router = useRouter();
  const {
    tin = '',
    requestId = '',
    trackingNumber = '',
    submittedOn = '',
  } = router.getLocation()?.state || {};

  const {
    pageIndex,
    nextPage,
    previousPage,
    goToPage,
    canNextPage,
    canPreviousPage,
  } = usePaginationV2({
    pages: STEPS.length,
  });

  const { documentIds, isDocumentUploading, updateSubmissionSaga } =
    useTinRegistration.state();
  const { isCheckoutUser, phone, uuid } = useUser.state();

  const isSuperUser = storage.local.get(uuid);

  const {
    uploadProof,
    setDocumentId,
    setDocumentLoading,
    submitUpdateSubmission,
  } = useTinRegistration.actions();

  const [submitModalStatus, setSubmitModalStatus] = useState(null); // null | 'success' | 'error'
  const [stepAlert, setStepAlert] = useState(false); // false | 'update-info-no-update' | true
  const [addrValidationCount, setAddrValidationCount] = useState(0);
  const [servicingAddresses, setServicingAddresses] = useState([]);
  const [servicingAddressErrors, setServicingAddressErrors] = useState(
    new Set()
  );
  const [doc360UploadErrors, setDoc360UploadErrors] = useState({});

  useEffect(() => {
    return () => {
      // Clears this page's form data and associated document IDs from the
      // shared form context when navigating away, so stale data does not persist.

      // Using setValue instead of reset because reset only operates on currently
      // registered fields — by unmount time, children have already unregistered.
      // setValue writes directly to the form store regardless of fields registration.
      abyssForm.setValue('w9', undefined);
      ADDR_SLOT_IDS.forEach((id) => {
        abyssForm.setValue(`secondaryServicing[${id}]`, undefined);
      });
      abyssForm.setValue('attestation', undefined);

      setDocumentId({ id: 'w9.file', value: undefined });
      ADDR_SLOT_IDS.forEach((id) => {
        setDocumentId({
          id: `secondaryServicing[${id}].file`,
          value: undefined,
        });
      });
    };
  }, []);

  const navigateHome = () => {
    router.navigate('/');
  };

  useEffect(() => {
    if (!tin) {
      router.navigate('/');
    }
  }, []);

  if (!tin) {
    return null;
  }

  const handleDoc360Uploads = async () => {
    setDoc360UploadErrors({});

    const abyssFormVals = abyssForm.getValues();

    const filesToUpload = UPLOAD_SLOTS.filter(
      ({ model }) => !documentIds?.[model]
    )
      .map(({ model, getFile }) => ({ model, file: getFile(abyssFormVals) }))
      .filter(({ file }) => file);

    // NOTE: All of the filesToUpload's file value should be real "File" object - instanceof File.
    // If not, something is wrong with the Abyss FileUpload output or
    // the form values are in an unexpected shape — uploading would have caused an API error.

    const results = await Promise.allSettled(
      filesToUpload.map(
        (fileObj) =>
          new Promise((resolve, reject) => {
            setDocumentLoading({ model: fileObj.model, value: true });

            uploadProof(
              {
                billingTin: tin,
                requestId,
                fileType: getFileType(fileObj.model),
                trackingNumber,
                file: fileObj.file,
                test: isCheckoutUser,
                model: fileObj.model,
              },
              (resp) => {
                setDocumentLoading({ model: fileObj.model, value: false });

                if (resp?.doc360Id) {
                  setDocumentId({ id: fileObj.model, value: resp.doc360Id });

                  resolve();
                } else {
                  setDoc360UploadErrors((curr) => ({
                    ...curr,
                    [fileObj.model]:
                      'Upload failed: A document ID could not be generated. Please try again later. (D-001)',
                  }));

                  event('API_TRANSACTION_ERROR', {
                    code: 'DOC360_UPLOAD_NO_ID',
                    message: 'Doc 360 upload returned with no id.',
                  });

                  reject(new Error('No doc360Id returned'));
                }
              },
              (error) => {
                setDocumentLoading({ model: fileObj.model, value: false });

                event('API_TRANSACTION_ERROR', {
                  code: error?.faultCode,
                  message: error?.message,
                });

                setDoc360UploadErrors((curr) => ({
                  ...curr,
                  [fileObj.model]: getUploadErrorMessage(error),
                }));

                reject(error);
              }
            );
          })
      )
    );

    const failedModels = new Set(
      results
        .map((r, i) =>
          r.status === 'rejected' ? filesToUpload[i].model : null
        )
        .filter(Boolean)
    );

    return {
      allUploadsOk: failedModels.size === 0,
      failedModels,
    };
  };

  const goNext = async () => {
    const abyssFormVals = abyssForm.getValues();

    const validateModel = (model) =>
      new Promise((resolve) =>
        abyssForm.validate(
          model,
          () => resolve(true),
          () => resolve(false)
        )
      );

    if (pageIndex === UPDATE_INFO_STEP_INDEX) {
      const hasW9Files = abyssFormVals.w9?.file.length > 0;
      const hasAnyServicingAddr = servicingAddresses.length > 0;

      // 1. No update at all
      if (!hasW9Files && !hasAnyServicingAddr) {
        setStepAlert('update-info-no-update');
        return;
      }

      // 2. Validate all sections in parallel so all field errors show at once.
      const [w9Ok, ...addrValidations] = await Promise.all([
        hasW9Files ? validateModel('w9') : Promise.resolve(true),
        ...ADDR_SLOT_IDS.map((id) =>
          servicingAddresses.includes(id)
            ? validateModel(`secondaryServicing[${id}]`)
            : Promise.resolve(true)
        ),
      ]);

      const updateInfoValidationsOk = w9Ok && addrValidations.every(Boolean);

      if (updateInfoValidationsOk) {
        setStepAlert(false);
        setServicingAddressErrors(new Set());

        const { allUploadsOk, failedModels } = await handleDoc360Uploads();

        if (allUploadsOk) {
          nextPage();
        } else {
          setStepAlert(true);

          const addrErrors = new Set();
          ADDR_SLOT_IDS.forEach((id) => {
            if (failedModels.has(`secondaryServicing[${id}].file`))
              addrErrors.add(id);
          });
          setServicingAddressErrors(addrErrors);
          setAddrValidationCount((c) => c + 1);
        }
      } else {
        setStepAlert(true);

        const addrErrors = new Set();
        ADDR_SLOT_IDS.forEach((id, i) => {
          if (
            servicingAddresses.includes(id) &&
            (!addrValidations[i] ||
              doc360UploadErrors[`secondaryServicing[${id}].file`])
          )
            addrErrors.add(id);
        });
        setServicingAddressErrors(addrErrors);
        setAddrValidationCount((c) => c + 1);
      }
    } else {
      nextPage();
    }
  };

  const submitUpdate = () => {
    abyssForm.validate('attestation', () => {
      const abyssFormVals = abyssForm.getValues();

      const w9Files = Array.isArray(abyssFormVals.w9?.file)
        ? abyssFormVals.w9.file
        : [];
      const hasValidW9FileCount = w9Files.length === 1;
      const w9Doc360Id = documentIds?.['w9.file'] || null;

      const address = servicingAddresses
        .map((id) => {
          const addr = abyssFormVals.secondaryServicing?.[id];
          if (!addr) return undefined;

          const addrFiles = Array.isArray(addr?.file) ? addr.file : [];
          const hasValidFileCount = addrFiles.length === 1;
          const addrDoc360Id =
            documentIds?.[`secondaryServicing[${id}].file`] || null;

          return {
            streetLine1: addr.streetLine1,
            streetLine2: addr.streetLine2,
            city: addr.city,
            state: addr.state,
            zipCode: addr.zipCode,
            type: `SecondaryService${id + 1}`,
            doc360Id: hasValidFileCount && addrDoc360Id ? addrDoc360Id : null,
            fileName:
              hasValidFileCount && addrDoc360Id ? addrFiles[0].name : null,
          };
        })
        .filter(Boolean);

      const reqBody = {
        billingTin: tin,
        trackingNumber,
        w9Details:
          hasValidW9FileCount && w9Doc360Id
            ? {
                doc360Id: w9Doc360Id,
                fileName: w9Files[0].name,
              }
            : null,
        address,
        attestationDetails: {
          name: abyssFormVals.attestation?.submitterName,
          phoneNumber: phone,
          email: abyssFormVals.attestation?.emailAddress,
        },
      };

      submitUpdateSubmission(
        reqBody,
        () => {
          setSubmitModalStatus('success');
        },
        () => {
          setSubmitModalStatus('error');
        }
      );
    });
  };

  const isUploading = Object.values(isDocumentUploading || {}).some(Boolean);

  return (
    <Loading isLoading={isUploading || updateSubmissionSaga.isLoading}>
      <PageContainer>
        <Layout.Group alignLayout="between">
          <PageHeading>Update submission for TIN: {tin}</PageHeading>

          <V2Button
            variant="outline"
            size="$sm"
            icon={{ icon: 'arrow_back', position: 'leading' }}
            onClick={navigateHome}
          >
            Return to dashboard
          </V2Button>
        </Layout.Group>

        <PageText>
          If you need to upload another W-9 or add additional servicing
          addresses, you can update your submission.
        </PageText>

        <ContentPanel>
          <V2StepTracker
            steps={STEPS}
            currentStep={pageIndex + 1}
            hideStepNumbers
            hideStepStatus
          />

          {stepAlert && (
            <V2Alert
              status="error"
              title={
                stepAlert === 'update-info-no-update'
                  ? 'Please update your W-9 and/or add a servicing address before continuing.'
                  : 'Please review and correct any errors or missing required information to continue.'
              }
              dismissible={false}
              inline
            />
          )}

          {/* Always mounted so V2FileUpload internal file list state survives
           tab switches as V2 doesn't have value prop to get back the file list like V1.
           Display:none hides it without unmounting. */}
          <div
            style={{
              display:
                pageIndex === UPDATE_INFO_STEP_INDEX ? 'contents' : 'none',
            }}
          >
            <UpdateInformation
              servicingAddresses={servicingAddresses}
              setServicingAddresses={setServicingAddresses}
              servicingAddressErrors={servicingAddressErrors}
              doc360UploadErrors={doc360UploadErrors}
              setDoc360UploadErrors={setDoc360UploadErrors}
              addrValidationCount={addrValidationCount}
            />
          </div>

          {pageIndex === REVIEW_STEP_INDEX && (
            <UpdateReview onEdit={() => goToPage(UPDATE_INFO_STEP_INDEX)} />
          )}

          {pageIndex === ATTEST_STEP_INDEX && (
            <UpdateAttestation submittedOn={submittedOn} />
          )}

          <ButtonRow>
            {canPreviousPage ? (
              <V2Button variant="outline" onClick={previousPage}>
                Back
              </V2Button>
            ) : (
              <CancelText onClick={navigateHome}>Cancel</CancelText>
            )}
            {canNextPage ? (
              <V2Button onClick={goNext}>Continue</V2Button>
            ) : (
              <V2Button
                variant={!!isSuperUser ? 'outline' : 'filled'}
                onClick={submitUpdate}
                isDisabled={!!isSuperUser}
              >
                Submit
              </V2Button>
            )}
          </ButtonRow>
        </ContentPanel>

        <V2ModalDialog
          isOpen={submitModalStatus !== null}
          hideClose
          closeOnClickOutside={false}
          size="720px"
          css={MODAL_CSS}
          header={
            <>
              <IconBrand
                icon={submitModalStatus === 'success' ? 'checkmark' : 'alert'}
                size="70px"
              />
              {submitModalStatus === 'success'
                ? 'Additional information received'
                : 'Submission unsuccessful'}
            </>
          }
        >
          {submitModalStatus === 'success'
            ? 'Your submission has been updated. You will receive an email once the review is complete.'
            : 'There was an error submitting your update. Please try again.'}
          <V2Button
            icon={
              submitModalStatus === 'success'
                ? { icon: 'arrow_back', position: 'leading' }
                : undefined
            }
            onClick={() =>
              submitModalStatus === 'success'
                ? navigateHome()
                : setSubmitModalStatus(null)
            }
            variant={submitModalStatus === 'success' ? 'filled' : 'outline'}
          >
            {submitModalStatus === 'success' ? 'Return to dashboard' : 'Close'}
          </V2Button>
        </V2ModalDialog>
      </PageContainer>
    </Loading>
  );
};
